import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

// 行き先と違う URL で遷移が完了した印（URL としてはありえない値）
const COMPLETED_ELSEWHERE = "\u0000completed-elsewhere";

type RouteEventHandler = (url: string, options: { shallow: boolean }) => void;

/** next/router の router.events のうち、ここで使う部分 */
export type RouterEventsLike = {
  on: (type: "routeChangeStart" | "routeChangeComplete" | "routeChangeError", handler: RouteEventHandler) => void;
  off: (type: "routeChangeStart" | "routeChangeComplete" | "routeChangeError", handler: RouteEventHandler) => void;
};

/**
 * 画面遷移の読み込み中の表示と、ページごとの jotai の store のキー。
 *
 * - **画面遷移（shallow でない）**: 読み込み中を出し、**新しい画面を描く瞬間に 1 回だけ** store を作り直す
 *   （画面ごとに atom を作り直す。#2131）。開始時は行き先を覚えるだけにし、ルーターの URL が行き先に変わった描画で
 *   キーを変える。出ていく画面を作り直さず、新しい画面も古い store で一度マウントされることがない（レビュー指摘）
 * - **失敗・中断**: 同じ画面に留まるので、行き先を忘れるだけで store は作り直さない（画面の状態を消さない）
 * - **shallow な遷移**（同じ画面の中でクエリだけ変える。タブ・絞り込みを URL に残すなど）: 何もしない。
 *   読み込み中を出すと画面全体が隠れ、store を作り直すと画面の状態が消える
 * - リダイレクトなどで行き先と実際の URL が食い違って完了したときだけ、完了時に作り直す（保険）
 *
 * 以前は JotaiProvider が描画のたびに新しいキーを振っていたため、shallow な遷移でも画面が作り直されていた。
 * その回避に `history.replaceState` で Next の外から URL を書き換える画面があった。
 *
 * @param asPath ルーターの今の URL（router.asPath）。新しい画面が描かれる瞬間を知るのに使う
 */
export const usePageTransition = (events: RouterEventsLike, asPath: string) => {
  const [pageLoading, setPageLoading] = useState(false);
  const [storeKey, setStoreKey] = useState(() => uuidv4());
  // 遷移中（shallow でない）の行き先。新しい画面を描いたら null に戻す。
  // 行き先と違う URL で完了したときは COMPLETED_ELSEWHERE にして、次の描画で作り直す
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  // 描画中に合わせる（https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes）。
  // この描画は確定前に作り直されるので、新しい画面は最初から新しいキーでマウントされる
  if (pendingUrl !== null && (asPath === pendingUrl || pendingUrl === COMPLETED_ELSEWHERE)) {
    setPendingUrl(null);
    setStoreKey(uuidv4());
  }

  useEffect(() => {
    const handleStart: RouteEventHandler = (url, { shallow }) => {
      if (shallow) return;
      setPageLoading(true);
      setPendingUrl(url);
    };
    const handleComplete: RouteEventHandler = (_url, { shallow }) => {
      setPageLoading(false);
      if (shallow) return;
      // 行き先どおりの URL で描いていれば、もう null になっている。残っていれば食い違ったので、次の描画で作り直す
      setPendingUrl((pending) => (pending === null ? null : COMPLETED_ELSEWHERE));
    };
    const handleError: RouteEventHandler = () => {
      setPageLoading(false);
      setPendingUrl(null);
    };

    events.on("routeChangeStart", handleStart);
    events.on("routeChangeComplete", handleComplete);
    events.on("routeChangeError", handleError);
    return () => {
      events.off("routeChangeStart", handleStart);
      events.off("routeChangeComplete", handleComplete);
      events.off("routeChangeError", handleError);
    };
  }, [events]);

  return { pageLoading, storeKey };
};
